import type { PricingItem } from '@/types'

type LaborRateInput = Pick<PricingItem, 'rate' | 'overtimeRate' | 'publicWorksRate' | 'serviceRates'>

/** Standard fallback overtime multiplier when an item carries no derivable OT ratio. */
const DEFAULT_OT_MULTIPLIER = 1.5

/**
 * Resolve the effective regular + overtime hourly rates for a labor pricing item,
 * honoring per-service overrides (`serviceRates`) and public-works pricing.
 *
 * Pricing items store a regular `rate`, an `overtimeRate`, and a `publicWorksRate`,
 * but NO dedicated public-works overtime rate. Previously, a public-works job raised
 * the regular rate to `publicWorksRate` while leaving overtime at the standard rate —
 * so OT came in CHEAPER than the elevated regular rate (npg bug, June 2026). We instead
 * scale overtime to the public-works rate using the item's own OT multiplier
 * (overtimeRate / rate, typically 1.5x), preserving each contractor's configured ratio.
 */
export function resolveLaborRates(
  item: LaborRateInput,
  serviceFilter?: string,
  isPublicWorks?: boolean,
): { rate: number; overtimeRate: number } {
  const svcRates = serviceFilter ? item.serviceRates?.[serviceFilter] : undefined
  const baseRate = svcRates ? svcRates.rate : item.rate
  const baseOvertimeRate = svcRates ? svcRates.overtimeRate : (item.overtimeRate || 0)

  if (isPublicWorks) {
    const pwRate = svcRates ? svcRates.publicWorksRate : item.publicWorksRate
    if (pwRate) {
      const otMultiplier = baseRate > 0 ? baseOvertimeRate / baseRate : DEFAULT_OT_MULTIPLIER
      return { rate: pwRate, overtimeRate: pwRate * otMultiplier }
    }
  }

  return { rate: baseRate, overtimeRate: baseOvertimeRate }
}
